import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { tourSteps } from '@/components/tour/tourSteps'

const STORAGE_KEY = 'gg_app_tour'
const ACTIVE_KEY = 'ggGuidedTourActive'
const STEP_KEY = 'ggGuidedTourStep'
const VERSION_KEY = 'ggGuidedTourVersion'
const TOUR_ROUTES = ['/', '/learn', '/tools', '/help']

type TourState = { isActive: boolean; currentStepIndex: number; hasCompleted: boolean }
type AppTourContextValue = TourState & {
  startTour: () => void
  nextStep: () => void
  previousStep: () => void
  skipTour: () => void
  endTour: () => void
}
const AppTourContext = createContext<AppTourContextValue | null>(null)

function readStorage(pathname: string, search: string): TourState {
  const state: TourState = { isActive: false, currentStepIndex: 0, hasCompleted: false }
  if (!TOUR_ROUTES.includes(pathname)) return state
  const requested = new URLSearchParams(search).get('tour') === 'active'
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')
    state.hasCompleted = saved?.hasCompleted === true
    // Resume this session, or migrate an unfinished tour from the old overlay.
    state.isActive = requested || sessionStorage.getItem(ACTIVE_KEY) === '1' ||
      (saved?.version !== 2 && saved?.isActive === true && !state.hasCompleted)
    const step = Number(sessionStorage.getItem(STEP_KEY))
    if (sessionStorage.getItem(VERSION_KEY) === '2' && Number.isInteger(step) && step >= 0 && step < tourSteps.length) {
      state.currentStepIndex = step
    }
  } catch {
    state.isActive = requested
  }
  return state
}

function writeStorage(state: TourState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, version: 2 }))
  } catch { /* The introduction also works when browser storage is unavailable. */ }
  try {
    if (state.isActive) {
      sessionStorage.setItem(ACTIVE_KEY, '1')
      sessionStorage.setItem(STEP_KEY, String(state.currentStepIndex))
      sessionStorage.setItem(VERSION_KEY, '2')
    } else {
      sessionStorage.removeItem(ACTIVE_KEY)
      sessionStorage.removeItem(STEP_KEY)
      sessionStorage.removeItem(VERSION_KEY)
    }
  } catch { /* State remains available in memory. */ }
}

export function AppTourProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [state, setState] = useState(() => readStorage(location.pathname, location.searchStr))

  useEffect(() => { writeStorage(state) }, [state])

  useEffect(() => {
    const saved = readStorage(location.pathname, location.searchStr)
    setState(previous => {
      if (!TOUR_ROUTES.includes(location.pathname)) {
        return previous.isActive ? { ...previous, isActive: false, currentStepIndex: 0 } : previous
      }
      return saved.isActive && !previous.isActive ? saved : previous
    })
  }, [location.pathname, location.searchStr])

  // Old tour links and replay entry points all open over the dashboard.
  useEffect(() => {
    if (state.isActive && TOUR_ROUTES.includes(location.pathname) && location.pathname !== '/') {
      navigate({ to: '/', search: { tour: 'active', invitationcode: undefined, __clerk_ticket: undefined }, replace: true })
    }
  }, [state.isActive, location.pathname, navigate])

  const startTour = useCallback(() => {
    const next = { isActive: true, currentStepIndex: 0, hasCompleted: false }
    writeStorage(next)
    setState(next)
    navigate({ to: '/', search: { tour: 'active', invitationcode: undefined, __clerk_ticket: undefined } })
  }, [navigate])

  const nextStep = useCallback(() => {
    setState(previous => ({ ...previous, currentStepIndex: Math.min(previous.currentStepIndex + 1, tourSteps.length - 1) }))
  }, [])
  const previousStep = useCallback(() => {
    setState(previous => ({ ...previous, currentStepIndex: Math.max(previous.currentStepIndex - 1, 0) }))
  }, [])
  const endTour = useCallback(() => {
    const next = { isActive: false, currentStepIndex: 0, hasCompleted: true }
    writeStorage(next)
    setState(next)
    navigate({ to: '/', search: { invitationcode: undefined, __clerk_ticket: undefined }, replace: true })
  }, [navigate])

  return (
    <AppTourContext.Provider value={{ ...state, startTour, nextStep, previousStep, skipTour: endTour, endTour }}>
      {children}
    </AppTourContext.Provider>
  )
}

export function useAppTour() {
  const context = useContext(AppTourContext)
  if (!context) throw new Error('useAppTour must be used within AppTourProvider')
  return context
}
